import type { SideNavigationProps } from '@amzn/awsui-components-core/side-navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useStarGridText } from 'src/components/star-grid/hooks/useStarGridText';
import { useStarGridNavPreference, type NavPreferenceScope } from 'src/components/star-grid/hooks/useStarGridNavPreference';
import type { NavigationReport } from 'src/components/star-grid/navigation.types';
import {
  canAddCustomNavGroup,
  groupNavReports,
  navGroupLabel,
  navGroupOptions,
  REPORTS_NAV_BLOCK_ID,
  withCustomNavGroup,
  withMovedNavGroup,
  withoutCustomNavGroup,
  withNavGroupExpanded,
  withNavReportGroup,
  type StarGridNavAssignment,
  type StarGridNavGroupOption
} from 'src/components/star-grid/utils/starGridNavGroups';
import { navSectionGroupId, starGridReportsNavGroup } from 'src/components/star-grid/utils/starGridNavItems';

/** Where the Reports row navigates: the StarGrid home, which is the screen that lists the reports. */
const REPORTS_HREF = '#home';

export interface UseStarGridNavGroupsResult {
  /**
   * The single Reports row for `SideNavigation`, with the grouping nested inside it. `undefined` when
   * there are no reports.
   */
  readonly reportsNavGroup: SideNavigationProps.LinkGroup | undefined;
  readonly organizeOpen: boolean;
  readonly openOrganize: () => void;
  readonly closeOrganize: () => void;
  /** Persists a group the user collapsed or expanded in the nav itself. */
  readonly onNavSectionToggled: (detail: SideNavigationProps.ChangeDetail) => void;
  /** Whether the Reports block itself is open; persisted with the groups' collapse state. */
  readonly reportsExpanded: boolean;
  readonly setReportsExpanded: (expanded: boolean) => void;
  /** One row per visible report, in the nav's own order, for the Organize Reports list. */
  readonly assignments: readonly StarGridNavAssignment[];
  /** Every section in nav order, empty ones included, so a new section is visible and reorderable. */
  readonly groupOptions: readonly StarGridNavGroupOption[];
  readonly newGroupName: string;
  /** Whether `newGroupName` would add a section, rather than be ignored as blank or taken. */
  readonly canAddGroup: boolean;
  readonly setNewGroupName: (value: string) => void;
  readonly addGroup: () => void;
  readonly setReportGroup: (report: NavigationReport, groupId: string) => void;
  readonly moveSection: (groupId: string, offset: number) => void;
  readonly removeGroup: (groupId: string) => void;
  readonly resetGroups: () => void;
  readonly canReset: boolean;
  /** Whether this user may switch the modal to editing the default for everyone. */
  readonly canEditDefault: boolean;
  readonly scope: NavPreferenceScope;
  readonly setScope: (scope: NavPreferenceScope) => void;
  /** Why the last change did not reach the service, if it did not. */
  readonly saveError: string | undefined;
}

/**
 * The grouped Reports nav plus the controls that let a user rearrange it.
 *
 * The arrangement itself is owned by `useStarGridNavPreference`, which persists it per user through
 * the StarGrid service. This hook is the view model over it: it turns the arrangement into nav items
 * and into the rows the Organize Reports modal edits.
 */
export const useStarGridNavGroups = (reports: readonly NavigationReport[], activeReportId: number | undefined): UseStarGridNavGroupsResult => {
  const text = useStarGridText();
  const { preference, hasOwnArrangement, canEditDefault, scope, setScope, saveError, update, reset } = useStarGridNavPreference();
  const [organizeOpen, setOrganizeOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');

  const groups = useMemo(() => groupNavReports(reports, preference), [reports, preference]);
  const label = useCallback((groupId: string) => navGroupLabel(groupId, preference, text), [preference, text]);

  // The group holding the open report. Auto-expanded once each time the active report changes, then
  // the user's own collapse wins — so the nav never fights a deliberate collapse of the active group.
  const activeGroupId = useMemo(
    () =>
      activeReportId === undefined ? undefined : groups.find((group) => group.reports.some((report) => report.pnl_report_id === activeReportId))?.id,
    [groups, activeReportId]
  );
  const [autoExpandedGroupId, setAutoExpandedGroupId] = useState<string | undefined>(undefined);
  useEffect(() => {
    setAutoExpandedGroupId(activeGroupId);
  }, [activeReportId, activeGroupId]);

  const collapsedForNav = useMemo(
    () => (autoExpandedGroupId ? preference.collapsed.filter((id) => id !== autoExpandedGroupId) : preference.collapsed),
    [preference.collapsed, autoExpandedGroupId]
  );

  const reportsNavGroup = useMemo(
    () =>
      starGridReportsNavGroup({
        groups,
        allReports: reports,
        collapsedGroupIds: collapsedForNav,
        label,
        reportsText: text('navReportsSection'),
        reportsHref: REPORTS_HREF
      }),
    [groups, reports, collapsedForNav, label, text]
  );

  const assignments = useMemo(() => groups.flatMap((group) => group.reports.map((report) => ({ report, groupId: group.id }))), [groups]);

  return {
    reportsNavGroup,
    organizeOpen,
    openOrganize: () => setOrganizeOpen(true),
    // Back to the user's own on close: nav collapse toggles outside the modal must never edit the default.
    closeOrganize: () => {
      setOrganizeOpen(false);
      setScope('mine');
    },
    onNavSectionToggled: (detail) => {
      const groupId = navSectionGroupId(detail.item);
      if (groupId === undefined) return;
      // A user collapse of the auto-expanded active group drops the transient so it does not reopen.
      if (!detail.expanded && groupId === autoExpandedGroupId) setAutoExpandedGroupId(undefined);
      update((current) => withNavGroupExpanded(current, groupId, detail.expanded));
    },
    reportsExpanded: !preference.collapsed.includes(REPORTS_NAV_BLOCK_ID),
    setReportsExpanded: (expanded) => update((current) => withNavGroupExpanded(current, REPORTS_NAV_BLOCK_ID, expanded)),
    assignments,
    groupOptions: navGroupOptions(preference, text),
    newGroupName,
    canAddGroup: canAddCustomNavGroup(preference, newGroupName),
    setNewGroupName,
    addGroup: () => {
      update((current) => withCustomNavGroup(current, newGroupName));
      setNewGroupName('');
    },
    setReportGroup: (report, groupId) => update((current) => withNavReportGroup(current, report, groupId)),
    moveSection: (groupId, offset) => update((current) => withMovedNavGroup(current, groupId, offset)),
    removeGroup: (groupId) => update((current) => withoutCustomNavGroup(current, groupId)),
    resetGroups: reset,
    canReset: hasOwnArrangement,
    canEditDefault,
    scope,
    setScope,
    saveError
  };
};
